import JSzip from "jszip";
import { saveAs } from "file-saver";
import { ZplTemplate } from "../config/newZebraTemplate";
import { fetchLogos } from "./fetchLogos";
import { buildAdditionalInfoForZpl } from "./labelAdditionalInfo";
import { zplField } from "./zplSafe";
import i18n from "../i18n/index.js";

function getFormattedDate() {
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const date = new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  return `${day} - ${month}`;
}

// Pedido hijo de un cambio de producto: el repartidor entrega el reemplazo Y retira
// el producto original, y eso tiene que leerse en la etiqueta. Va en la franja libre
// entre la línea divisoria (^FO0,340, 2 de alto) y el QR / la comuna (y=380 / 400),
// a todo el ancho y centrada, así no pisa ningún otro campo. ASCII puro y sin "_":
// el campo lleva ^FH, donde el "_" abre un escape hexadecimal.
export const EXCHANGE_ZPL_LINE =
  "^FO40,348^A0N,28,28^FB720,1,0,C^FH^FDCAMBIO - RETIRAR PRODUCTO^FS";

/** Valor de {{exchangeLine}}: la línea de cambio solo si el paquete es hijo de un cambio. */
export const buildExchangeZplLine = (packageData) =>
  packageData?.exchangeOf?.packageId ? EXCHANGE_ZPL_LINE : "";

export const downloadZplFormat = async ({ defaultData, user }) => {
  let zplBase = ZplTemplate;

  const logos = await fetchLogos(user);
  if (!logos) {
    return false;
  }

  const totalCopiesRaw = Number(defaultData?.additionalLabels);
  const totalCopies = Number.isFinite(totalCopiesRaw) && totalCopiesRaw > 0 ? Math.floor(totalCopiesRaw) : 1;

  const zip = new JSzip();

  for (let i = 1; i <= totalCopies; i++) {
    let zplFormat = zplBase;
    const businessName = defaultData?.senderInfo?.businessName;
    const senderName = defaultData?.senderUser?.senderName ?? "";
    const senderLastName = defaultData?.senderUser?.senderLastName ?? "";
    const nameToUse = businessName || (senderName + " " + senderLastName);
    const receiverName = defaultData?.receiver_name ?? "";
    const receiverLastName = defaultData?.receiver_lastName ?? "";
    const receiverNameToUse = (receiverName + " " + receiverLastName);
    const fechaActual = getFormattedDate();
    const comunaVendedor = defaultData?.senderInfo?.senderMunicipality + ' ' + defaultData?.senderInfo?.senderState
    const logoClienteZpl = logos.logo.zplLogo || "";
    const transportistaName = defaultData?.deliveryInfo?.deliveryBusinessName;
    const state = defaultData?.shipping_address?.city?.name;

    zplFormat = zplField(
      zplFormat,
      "{{transportistaName}}",
      transportistaName
        .toString()
        .replace(/[a-zA-ZñÑáéíóúÁÉÍÓÚ]+/g, (match) => match.toUpperCase()),
    );

    zplFormat = zplField(zplFormat, "{{logoCliente}}", logoClienteZpl);

    zplFormat = zplField(
      zplFormat,
      "{{address}}",
      `${defaultData?.senderUser?.senderAddress?.slice(0, 60) ?? ""}`,
    );
    zplFormat = zplField(zplFormat, "{{address2}}", comunaVendedor);
    zplFormat = zplField(
      zplFormat,
      "{{city}}",
      `${defaultData?.shipping_address?.municipality?.name ?? i18n.t("Comuna no asignada")}`,
    );
    zplFormat = zplField(zplFormat, "{{state}}", `${state}`);
    zplFormat = zplField(zplFormat, "{{emitter.name}}", nameToUse);
    zplFormat = zplField(
      zplFormat,
      "{{emitter.phone}}",
      `${!!defaultData?.senderUser?.phone ? defaultData?.senderUser?.phone : ""}`,
    );
    zplFormat = zplField(zplFormat, "{{receiver.name}}", receiverNameToUse);
    zplFormat = zplField(
      zplFormat,
      "{{receiver.phone}}",
      `${defaultData?.receiver_phone ?? i18n.t("Teléfono no asignado")}`,
    );
    zplFormat = zplField(
      zplFormat,
      "{{receiver.address_line}}",
      `${defaultData?.shipping_address?.address_line?.slice(0, 50) ?? ""}`,
    );
    zplFormat = zplField(
      zplFormat,
      "{{receiver.address_line2}}",
      `${defaultData?.shipping_address?.address_line?.slice(50) ?? ""} ${
        defaultData?.shipping_address?.city?.name ?? ""
      }`,
    );
    zplFormat = zplField(
      zplFormat,
      "{{receiver.comment}}",
      buildAdditionalInfoForZpl(
        defaultData?.shipping_address?.comment,
        defaultData?.comments,
      ),
    );

    zplFormat = zplField(zplFormat, "{{packageKey}}", `${defaultData?._id}`);
    zplFormat = zplField(
      zplFormat,
      "{{followingNumber}}",
      `${defaultData?.followingNumber}`,
    );
    zplFormat = zplField(
      zplFormat,
      "{{comments}}",
      `${defaultData?.comments ?? i18n.t("Sin comentarios")}`,
    );
    zplFormat = zplField(zplFormat, "{{order_id}}", `${defaultData?.order_id ?? ""}`);
    zplFormat = zplField(zplFormat, "{{fecha}}", fechaActual);

    const bultoLine = totalCopies > 1
      ? `^FO420,300^A0N,28,28^FB720,1,0,L^FH^FDBULTO: ${i} DE ${totalCopies}^FS`
      : "";
    zplFormat = zplField(zplFormat, "{{bultoLine}}", bultoLine);
    zplFormat = zplField(zplFormat, "{{exchangeLine}}", buildExchangeZplLine(defaultData));

    zplFormat = zplFormat.replaceAll("ñ", "n");
    zplFormat = zplFormat.replaceAll("Ñ", "N");

    const suffix = totalCopies > 1 ? ` - bulto ${i} de ${totalCopies}` : "";
      zip.file(`Paquete OTS - ${defaultData._id}${suffix}.zpl`, zplFormat);
  }

  const content = await zip.generateAsync({ type: "blob" });
  await saveAs(content, "Paquetes OTS.zip");
  return true;
};

export const multipleDownloadZPLFile = async ({ packages, user }) => {
  
  const logos = await fetchLogos(user);
  if (!logos) {
    return false;
  }

  let zplFormatAllPackages = "";
  

  for (const packageData of packages) {
    const totalCopiesRaw = Number(packageData?.additionalLabels);
    const totalCopies = Number.isFinite(totalCopiesRaw) && totalCopiesRaw > 0
      ? Math.floor(totalCopiesRaw)
      : 1;

    for (let copyIndex = 1; copyIndex <= totalCopies; copyIndex++) {
      let zplFormat = ZplTemplate;

      const businessName = packageData?.senderInfo?.businessName;
      const senderName = packageData?.senderUser?.senderName ?? "";
      const senderLastName = packageData?.senderUser?.senderLastName ?? "";
      const nameToUse = businessName || (senderName + " " + senderLastName);
      const receiverName = packageData?.receiver_name ?? "";
      const receiverLastName = packageData?.receiver_lastName ?? "";
      const receiverNameToUse = receiverName + " " + receiverLastName;
      const fechaActual = getFormattedDate();
      const comunaVendedor = (packageData?.senderInfo?.senderMunicipality || "") + " " + (packageData?.senderInfo?.senderState || "");
      const logoClienteZpl = logos.logo.zplLogo || "";
      const transportistaName = packageData?.deliveryInfo?.deliveryBusinessName || "";
      const destinationMunicipality = (packageData?.shipping_address?.municipality?.name ?? '').toUpperCase();
      const destinationState = (packageData?.shipping_address?.city?.name ?? '').toUpperCase();
      const destinationAddress = ((packageData?.shipping_address?.street_name || "") + ' ' + (packageData?.shipping_address?.street_number || "") + ', ' + (packageData?.shipping_address?.municipality?.name || "") + ', ' + (packageData?.shipping_address?.city?.name || "")).trim();
      const comment = buildAdditionalInfoForZpl(
        packageData?.shipping_address?.comment,
        packageData?.comments
      );


      zplFormat = zplField(
        zplFormat,
        "{{transportistaName}}",
        (transportistaName || "")
          .toString()
          .replace(/[a-zA-ZñÑáéíóúÁÉÍÓÚ]+/g, (match) => match.toUpperCase()),
      );

      zplFormat = zplField(zplFormat, "{{logoCliente}}", logoClienteZpl);

      zplFormat = zplField(
        zplFormat,
        "{{address}}",
        `${packageData?.senderInfo?.senderAddress?.slice(0, 60) ?? ""}`,
      );
      zplFormat = zplField(zplFormat, "{{address2}}", comunaVendedor);
      zplFormat = zplField(
        zplFormat,
        "{{city}}",
        `${destinationMunicipality ?? i18n.t("Comuna no asignada")}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{state}}",
        `${destinationState ?? i18n.t("Comuna no asignada")}`,
      );
      zplFormat = zplField(zplFormat, "{{emitter.name}}", nameToUse);
      zplFormat = zplField(
        zplFormat,
        "{{emitter.phone}}",
        `${!!packageData?.senderUser?.phone ? packageData?.senderUser?.phone : ""}`,
      );
      zplFormat = zplField(zplFormat, "{{receiver.name}}", receiverNameToUse);
      zplFormat = zplField(
        zplFormat,
        "{{receiver.phone}}",
        `${packageData?.receiver_phone ?? i18n.t("Teléfono no asignado")}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{receiver.address_line}}",
        `${(destinationAddress || "").slice(0, 60)}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{receiver.address_line2}}",
        `${(destinationAddress || "").slice(60)}`,
      );
      zplFormat = zplField(zplFormat, "{{receiver.comment}}", comment);

      zplFormat = zplField(zplFormat, "{{packageKey}}", `${packageData?._id}`);
      zplFormat = zplField(
        zplFormat,
        "{{followingNumber}}",
        `${packageData?.followingNumber}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{comments}}",
        `${packageData?.shipping_address?.comment ?? i18n.t("Sin comentarios")}`,
      );
      zplFormat = zplField(zplFormat, "{{order_id}}", `${packageData?.order_id ?? ""}`);
      zplFormat = zplField(zplFormat, "{{fecha}}", fechaActual);

      const bultoLine =
        totalCopies > 1
          ? `^FO420,300^A0N,28,28^FB720,1,0,L^FH^FDBULTO: ${copyIndex} DE ${totalCopies}^FS`
          : "";

      zplFormat = zplField(zplFormat, "{{bultoLine}}", bultoLine);
      zplFormat = zplField(zplFormat, "{{exchangeLine}}", buildExchangeZplLine(packageData));

      zplFormat = zplFormat.replaceAll("ñ", "n");
      zplFormat = zplFormat.replaceAll("Ñ", "N");

      zplFormatAllPackages += zplFormat;
    }
  }

  return zplFormatAllPackages;
};
